import { useEffect, useState } from 'react';
import { useBookingDraft } from '@/contexts/BookingDraftContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FileEdit, X, Clock } from 'lucide-react';
import AdminBookingWizard from './AdminBookingWizard';

function formatTime(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function DraftChip({ draft, onRestore, onClose }) {
  const [elapsed, setElapsed] = useState(Date.now() - draft.createdAt);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Date.now() - draft.createdAt);
    }, 1000);
    return () => clearInterval(interval);
  }, [draft.createdAt]);

  // Calculate Progress (1/3, 2/3, 3/3)
  const d = draft.data;
  let stage = 0;
  if (d.siteKey && d.serviceId) stage = 1;
  if (stage === 1 && d.patientId) stage = 2; // if selected patient
  if (stage === 1 && d.patientMode === 'new' && d.patient.firstName && d.patient.phone) stage = 2;
  if (stage === 2 && d.confirmedDate && d.confirmedTime) stage = 3;

  const progressPercent = (stage / 3) * 100;
  const isDanger = elapsed > 5 * 60 * 1000; // 5 mins
  const isWarning = elapsed > 3 * 60 * 1000; // 3 mins

  const name = d.patientMode === 'selected' && d.patient?.firstName
    ? `${d.patient.firstName} ${d.patient.lastName?.charAt(0) || ''}.`
    : d.patientMode === 'new' && d.patient?.firstName
    ? `${d.patient.firstName} ${d.patient.lastName?.charAt(0) || ''}.`
    : 'New Caller';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
      className="pointer-events-auto flex items-center gap-3 bg-stone-900 text-white pl-2 pr-1 py-1 rounded-full shadow-xl shadow-stone-900/20 hover:bg-stone-800 transition-colors border border-stone-700 cursor-pointer group whitespace-nowrap shrink-0"
      onClick={() => onRestore(draft.id)}
    >
      {/* Progress Ring Avatar */}
      <div className="relative w-7 h-7 flex items-center justify-center shrink-0">
        <svg className="absolute inset-0 w-full h-full -rotate-90">
          <circle cx="14" cy="14" r="13" fill="none" stroke="currentColor" className="text-stone-700" strokeWidth="2" />
          <circle 
            cx="14" cy="14" r="13" fill="none" stroke="currentColor" 
            className="text-emerald-400 transition-all duration-500" 
            strokeWidth="2" 
            strokeDasharray={`${(progressPercent / 100) * 81.68} 81.68`} 
          />
        </svg>
        <FileEdit className="w-3.5 h-3.5 text-white z-10" />
      </div>

      <div className="flex flex-col py-0.5">
        <span className="text-xs font-semibold leading-none">{name}</span>
        <div className="flex items-center gap-1 mt-1 text-[10px] text-stone-400 font-mono">
          <Clock className={`w-3 h-3 ${isDanger ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-stone-500'}`} />
          <span className={isDanger ? 'text-red-400' : isWarning ? 'text-amber-400' : ''}>{formatTime(elapsed)}</span>
        </div>
      </div>

      <button
        className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ml-1 shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onClose(draft.id);
        }}
      >
        <X className="w-4 h-4 text-stone-300" />
      </button>
    </motion.div>
  );
}

export default function BookingDraftManager() {
  const { drafts, restoreDraft, closeDraft } = useBookingDraft();

  const minimisedDrafts = drafts.filter(d => d.status === 'minimised');
  const openDraft = drafts.find(d => d.status === 'open');

  return (
    <>
      {/* Floating Minimised Chips */}
      <div className="fixed top-16 sm:top-auto sm:bottom-6 left-4 right-4 sm:left-auto sm:right-6 z-[60] flex flex-row sm:flex-col gap-2 overflow-x-auto hide-scrollbar items-start sm:items-end pointer-events-none p-1">
        <AnimatePresence>
          {minimisedDrafts.map((draft) => (
            <DraftChip key={draft.id} draft={draft} onRestore={restoreDraft} onClose={closeDraft} />
          ))}
        </AnimatePresence>
      </div>

      {/* Full Open Modal */}
      <AnimatePresence>
        {openDraft && (
          <AdminBookingWizard key={openDraft.id} draftId={openDraft.id} />
        )}
      </AnimatePresence>
    </>
  );
}
